import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { changePlan } from '../../store/subscriptionSlice';
import { useDispatch } from 'react-redux';
import subscriptionService from '../../services/subscriptionService';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { extractPlanPriceId, createSubscriptionData } from '../../utils/subscriptionUtils';

// Stripe form component
const StripeForm = ({ onSubmit, isProcessing, actionType, onPaymentMethodChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);

  // Auto-capture payment method when card details change
  const handleCardChange = async (event) => {
    if (!stripe || !elements) return;

    // Clear any previous errors
    setPaymentError('');

    // If card is complete and valid, automatically create payment method
    if (event.complete && !event.error) {
      setIsCapturing(true);
      
      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement),
        });

        if (error) {
          setPaymentError(error.message);
          onPaymentMethodChange(null); // Clear payment method
        } else {
          // Successfully created payment method
          onPaymentMethodChange(paymentMethod.id);
          setPaymentError(''); // Clear any previous errors
        }
      } catch (error) {
        setPaymentError('Failed to create payment method');
        onPaymentMethodChange(null);
      } finally {
        setIsCapturing(false);
      }
    } else if (event.error) {
      setPaymentError(event.error.message);
      onPaymentMethodChange(null); // Clear payment method on error
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-300 rounded-lg p-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
          onChange={handleCardChange}
        />
      </div>
      
      {isCapturing && (
        <div className="flex items-center gap-2 text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span>Capturing payment method...</span>
        </div>
      )}
      
      {paymentError && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
          {paymentError}
        </div>
      )}
      
      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-blue-800">
          💳 <strong>Auto-capture:</strong> Your payment method will be automatically captured as you type your card details.
        </p>
      </div>
    </div>
  );
};

const AdminSubscriptionManagement = () => {
  const dispatch = useDispatch();
  const { 
    plans, 
    plansLoading,
    error,
    handleStartBillingSubscription,
    handleStartTrial,
    handleUpgradeSubscription,
    loadPlans
  } = useSubscription();
  
  const {
    users: adminUsers,
    usersLoading: adminLoading,
    usersError: adminError,
    loadUsers: loadAdminUsers
  } = useAdminUsers();
  
  const [stripe, setStripe] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [actionType, setActionType] = useState('change-plan');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [subscriptionResult, setSubscriptionResult] = useState(null);
  const [userSubscriptions, setUserSubscriptions] = useState({});
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);

  // Load admin users and plans on component mount
  const [hasLoaded, setHasLoaded] = useState(false);
  
  useEffect(() => {
    if (!hasLoaded) {
      setHasLoaded(true);
      loadAdminUsers({ page: 0, size: 1000 });
      loadPlans();
    }
  }, [hasLoaded, loadAdminUsers, loadPlans]);

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      try {
        // Replace with your actual Stripe publishable key
        const stripeInstance = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        setStripe(stripeInstance);
      } catch (error) {
      }
    };
    
    initStripe();
  }, []);

  // Load subscription data for users when admin users are loaded
  useEffect(() => {
    if (adminUsers && Array.isArray(adminUsers) && adminUsers.length > 0) {
      loadUserSubscriptionsForUsers();
    }
  }, [adminUsers]);

  // Function to load subscription data for all users
  const loadUserSubscriptionsForUsers = async () => {
    if (!adminUsers || !Array.isArray(adminUsers) || adminUsers.length === 0) return;
    
    setIsLoadingSubscriptions(true);
    const subscriptions = {};
    
    // Process users in batches of 3 with 400ms delay between batches
    const batchSize = 3;
    const delay = 400;
    
    for (let i = 0; i < adminUsers.length; i += batchSize) {
      const batch = adminUsers.slice(i, i + batchSize);
      
      // Process batch in parallel using the subscription service
      const batchPromises = batch.map(async (user) => {
        try {
          // Use the subscription service to get user subscription
          const response = await subscriptionService.getUserSubscription(user.id);
          console.log(`🔍 Subscription response for user ${user.id}:`, response);
          if (response.success && response.data) {
            subscriptions[user.id] = response.data;
          } else if (response.data === null) {
            // User has no subscription
            subscriptions[user.id] = null;
          }
        } catch (error) {
          console.warn(`Failed to load subscription for user ${user.id}:`, error);
        }
      });
      
      await Promise.all(batchPromises);
      
      // Add delay between batches (except for the last batch)
      if (i + batchSize < adminUsers.length) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    console.log('📊 Loaded user subscriptions:', subscriptions);
    setUserSubscriptions(subscriptions);
    setIsLoadingSubscriptions(false);
    
  };

  // Handle plan selection to extract required data
  const handlePlanSelection = (planCode) => {
    const plan = plans.find(p => p.planCode === planCode);
    
    if (plan) {
      // Try different possible field names for the Stripe price ID
      const planPriceId = plan.stripePriceId || plan.planPriceId || plan.priceId || plan.stripe_price_id;
      
      const planData = {

        planCode: plan.planCode,
        storageLimitSeconds: plan.storageLimitSeconds
      };
      setSelectedPlanData(planData);
    } else {
      setSelectedPlanData(null);
    }
    setSelectedPlan(planCode);
  };

  // Start subscription using backend billing endpoint
  const startSubscription = async (freeTier = false) => {
    if (!selectedUser || !selectedPlanData || (!freeTier && !paymentMethodId)) {
      throw new Error('Missing required data for subscription');
    }
    
    try {
      // Extract plan price ID using consolidated utility
      const planPriceId = extractPlanPriceId(selectedPlanData);
      
      // Create subscription data using consolidated utility
      const subscriptionData = createSubscriptionData({
        userId: parseInt(selectedUser),
        planCode: selectedPlanData.planCode,
        planPriceId: planPriceId,
        paymentMethodId: freeTier ? null : paymentMethodId,
        storageLimitSeconds: selectedPlanData.storageLimitSeconds
      });

      console.log('AdminSubscriptionManagement - subscriptionData:', subscriptionData); // Debug log

      // Use the appropriate admin endpoint based on action type
      let response;
      if (actionType === 'trial') {
        // Use admin trial endpoint
        response = await handleStartTrial(parseInt(selectedUser), selectedPlanData.planCode);
      } else if (actionType === 'upgrade') {
        // Use admin upgrade endpoint
        response = await handleUpgradeSubscription(parseInt(selectedUser), selectedPlanData.planCode);
      } else {
        // Use billing endpoint for other cases
        response = await handleStartBillingSubscription(subscriptionData);
      }
      
      if (response.payload) {
        console.log('AdminSubscriptionManagement - Response payload:', response.payload);
        console.log('AdminSubscriptionManagement - Response data:', response.payload?.data);
        console.log('AdminSubscriptionManagement - Client secret:', response.payload?.clientSecret || response.payload?.data?.clientSecret);
        
        // Handle client secret for 3D Secure if needed
        const clientSecret = response.payload.clientSecret || response.payload.data?.clientSecret;
        if (clientSecret && !freeTier) {
          console.log('AdminSubscriptionManagement - Confirming payment with clientSecret:', clientSecret);
          console.log('AdminSubscriptionManagement - Using paymentMethodId:', paymentMethodId);
          
          // Use paymentMethodId for payment confirmation
          const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethodId
          });
          
          if (error) {
            console.error('AdminSubscriptionManagement - Payment confirmation error:', error);
            console.error('AdminSubscriptionManagement - Error code:', error.code);
            console.error('AdminSubscriptionManagement - Payment intent status:', error.payment_intent?.status);
            throw new Error(`Payment confirmation failed: ${error.message}`);
          }
          
          if (paymentIntent.status === 'succeeded') {
            setSubscriptionResult({ ...response.payload, status: 'confirmed' });
            return { ...response.payload, status: 'confirmed' };
          } else {
            setSubscriptionResult({ ...response.payload, status: paymentIntent.status });
            return { ...response.payload, status: paymentIntent.status };
          }
        } else {
          // Success - show result
          setSubscriptionResult(response.payload);
          return response.payload;
        }
      } else {
        throw new Error('Failed to start subscription');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleAction = async () => {
    if (!selectedUser || !selectedPlan) return;
    
    setIsProcessing(true);
    let response = null;
    try {
      switch (actionType) {
        case 'start-free':
          // Use start free subscription endpoint
          response = await dispatch(changePlan({ userId: parseInt(selectedUser), planCode: 'FREE' }));
          break;
        case 'change-plan':
          // Use change plan endpoint (works for both new subscriptions and plan changes)
          response = await dispatch(changePlan({ userId: parseInt(selectedUser), planCode: selectedPlanData.planCode }));
          break;
        case 'cancel':
          // For cancel, we need the stripe subscription ID
          // This would need to be fetched from the user's current subscription
          throw new Error('Cancel functionality requires stripe subscription ID - use billing endpoints directly');
        default:
          break;
      }
      
      // Reset form
      setSelectedUser('');
      setSelectedPlan('');
      setActionType('change-plan');
      setSelectedPlanData(null);
      setPaymentMethodId('');
      setSubscriptionResult(null);
    } catch (error) {
      alert(`Action failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Add timeout to prevent infinite loading
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (plansLoading || adminLoading) {
        setLoadingTimeout(true);
      }
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(timer);
  }, [plansLoading, adminLoading]);

  if ((plansLoading || adminLoading) && !loadingTimeout) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          Admin Subscription Management
        </h1>
        <div className="text-center text-lg text-gray-600 p-8">
          <div className="mb-4">Loading...</div>
          <div className="text-sm text-gray-500">
            Loading plans: {plansLoading ? 'Yes' : 'No'}<br/>
            Loading admin users: {adminLoading ? 'Yes' : 'No'}
          </div>
        </div>
      </div>
    );
  }

  if (error || adminError) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          Admin Subscription Management
        </h1>
        <div className="text-center text-lg text-red-600 p-8">
          <div className="mb-4">Error occurred:</div>
          {error && <div className="mb-2">Plans Error: {error}</div>}
          {adminError && <div className="mb-2">Admin Users Error: {adminError}</div>}
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
        Admin Subscription Management
      </h1>
      
      {!stripe && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> Make sure to set the <code className="bg-yellow-100 px-1 rounded">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> environment variable for Stripe integration to work.
          </p>
        </div>
      )}
      
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-teal-500">
          Subscription Actions
        </h2>
        
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-sm">
                Action Type:
              </label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                disabled={isProcessing}
                className="p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="start-free">Start Free Subscription</option>
                <option value="change-plan">Change User Plan</option>
                <option value="cancel">Cancel Subscription</option>
              </select>
              <p className="text-sm text-gray-600 mt-1">
                {actionType === 'cancel' 
                  ? 'Cancel existing subscription for a user (requires Stripe subscription ID)' 
                  : actionType === 'start-free'
                  ? 'Start a free subscription for a user (no payment required)'
                  : 'Change any user\'s subscription plan (works for new subscriptions and plan changes)'
                }
              </p>
              {actionType === 'cancel' && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> For advanced subscription management (cancel, auto-renew), 
                    use the <strong>Billing Management</strong> tab which provides dedicated forms for these operations.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-sm">
                User:
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={isProcessing}
                className="p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Select a user</option>
                {adminUsers && Array.isArray(adminUsers) && adminUsers.length > 0 ? (
                  adminUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username || user.name || user.email} ({user.email})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No users available</option>
                )}
              </select>
            </div>
          </div>

          {/* Plan selection - only show for start-free and change-plan actions */}
          {(actionType === 'start-free' || actionType === 'change-plan') && (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-gray-800 text-sm">
                  Plan:
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => handlePlanSelection(e.target.value)}
                  disabled={isProcessing}
                  className="p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">Select a plan</option>
                  {plans.map(plan => (
                    <option key={plan.planCode} value={plan.planCode}>
                      {plan.name} ({plan.planCode}) - ${Number(plan.price.amount).toFixed(2)}/month
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Display extracted plan data - only show for start-free and change-plan actions */}
          {selectedPlanData && (actionType === 'start-free' || actionType === 'change-plan') && (
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <h4 className="font-semibold mb-3 text-blue-800">
                Plan Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-700">Plan Code:</span>
                  <p className="text-blue-600">{selectedPlanData.planCode}</p>
                </div>
                <div>
                  <span className="font-medium text-blue-700">Storage Limit:</span>
                  <p className="text-blue-600">{selectedPlanData.storageLimitSeconds} seconds</p>
                </div>
              </div>

            </div>
          )}

          {/* Admin action note - only show for start-free and change-plan actions */}
          {selectedPlanData && (actionType === 'start-free' || actionType === 'change-plan') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-blue-600 text-lg">🔧</span>
                <h4 className="font-semibold text-blue-800">
                  {actionType === 'start-free' ? 'Admin Free Subscription' : 'Admin Plan Change'}
                </h4>
              </div>
              <p className="text-blue-700 text-sm">
                {actionType === 'start-free' 
                  ? 'This will start a free subscription for the user immediately without requiring payment details.'
                  : 'This will change the user\'s subscription plan immediately without requiring payment details. Works for both new subscriptions and existing plan changes.'
                }
              </p>
            </div>
          )}

          <div className="flex flex-col gap-6">
            {/* Main action button - shows different text based on action type */}
            <div className="flex justify-center">
              <button
                onClick={handleAction}
                disabled={!selectedUser || (!selectedPlan && actionType !== 'cancel') || isProcessing}
                className={`px-8 py-4 border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:transform hover:enabled:-translate-y-1 hover:enabled:shadow-lg ${
                  actionType === 'start-free' ? 'bg-green-600 text-white hover:enabled:bg-green-700' :
                  actionType === 'change-plan' ? 'bg-blue-600 text-white hover:enabled:bg-blue-700' :
                  'bg-red-600 text-white hover:enabled:bg-red-700'
                }`}
              >
                {isProcessing ? 'Processing...' : 
                  actionType === 'start-free' ? 'Start Free Subscription' :
                  actionType === 'change-plan' ? 'Change User Plan' :
                  'Cancel Subscription'
                }
              </button>
            </div>

            {/* Additional info for actions */}
            {selectedPlanData && (actionType === 'start-free' || actionType === 'change-plan') && (
              <div className="text-center">
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-gray-700">
                    <strong>Note:</strong> {actionType === 'start-free' 
                      ? 'This will start a free subscription for the user immediately.'
                      : 'This will change the user\'s subscription plan immediately. If the user doesn\'t have a subscription, a new one will be created.'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Subscription Display */}
      {selectedUser && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-500">
            User Subscription Status
          </h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-blue-600 text-lg">👤</span>
              <h3 className="font-semibold text-blue-800">
                {adminUsers && Array.isArray(adminUsers) ? 
                  (adminUsers.find(u => u.id === parseInt(selectedUser))?.username || 
                   adminUsers.find(u => u.id === parseInt(selectedUser))?.name || 
                   adminUsers.find(u => u.id === parseInt(selectedUser))?.email) : 
                  `User ${selectedUser}`}
              </h3>
            </div>
            <p className="text-blue-700 text-sm">
              <strong>Email:</strong> {adminUsers && Array.isArray(adminUsers) ? 
                adminUsers.find(u => u.id === parseInt(selectedUser))?.email : 
                'N/A'}
            </p>
            <p className="text-blue-700 text-sm mt-2">
              <strong>Note:</strong> Current subscription details would be displayed here. 
              The change plan action will work for both new subscriptions and existing plan changes.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-teal-500">
          Available Plans
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map(plan => (
            <div key={plan.planCode} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">{plan.name}</h3>
                <span className="bg-teal-600 text-white px-3 py-1 rounded-xl text-xs font-semibold uppercase">
                  {plan.planCode}
                </span>
              </div>
              <div className="text-sm text-gray-600 mb-2">{plan.description}</div>
              <div className="text-2xl font-bold text-teal-600 mb-4">
                ${Number(plan.price.amount).toFixed(2)}/month
              </div>
              <div className="flex flex-wrap gap-2">
                {plan.features.slice(0, 3).map((feature, index) => (
                  <span key={index} className="bg-gray-200 text-gray-600 px-2 py-1 rounded-md text-sm">
                    {feature}
                  </span>
                ))}
                {plan.features.length > 3 && (
                  <span className="bg-orange-500 text-white px-2 py-1 rounded-md text-sm font-semibold">
                    +{plan.features.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Subscriptions Display */}
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 pb-2 border-b-2 border-teal-500">
            User Subscriptions
          </h2>
          <button
            onClick={loadUserSubscriptionsForUsers}
            disabled={isLoadingSubscriptions}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className={`w-4 h-4 ${isLoadingSubscriptions ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isLoadingSubscriptions ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {isLoadingSubscriptions ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading user subscriptions...</p>
          </div>
        ) : Object.keys(userSubscriptions).length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">📋</div>
            <p className="text-gray-600">No active subscriptions found</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.entries(userSubscriptions).map(([userId, subscription]) => {
              const user = adminUsers && Array.isArray(adminUsers) ? 
                adminUsers.find(u => u.id === parseInt(userId)) : 
                null;
              
              // Handle case where subscription is null (user has no subscription)
              if (!subscription) {
                return (
                  <div key={userId} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-800">
                          {user?.username || user?.name || user?.email}
                        </h4>
                        <p className="text-sm text-gray-600">{user?.email}</p>
                      </div>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                        No Subscription
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div><strong>Status:</strong> No active subscription</div>
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={userId} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        {user?.username || user?.name || user?.email}
                      </h4>
                      <p className="text-sm text-gray-600">{user?.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      subscription.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                      subscription.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                      subscription.status === 'EXPIRED' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {subscription.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div><strong>Plan:</strong> {subscription.plan?.name || subscription.plan?.planCode || subscription.planCode || 'Unknown'}</div>
                    <div><strong>Plan Code:</strong> {subscription.plan?.planCode || subscription.planCode || 'Unknown'}</div>
                    {subscription.startsAt && (
                      <div><strong>Started:</strong> {new Date(subscription.startsAt).toLocaleDateString()}</div>
                    )}
                    {subscription.endsAt && (
                      <div><strong>Ends:</strong> {new Date(subscription.endsAt).toLocaleDateString()}</div>
                    )}
                    {subscription.autoRenew !== undefined && (
                      <div><strong>Auto Renew:</strong> {subscription.autoRenew ? 'Yes' : 'No'}</div>
                    )}
                    {subscription.stripeSubscriptionId && (
                      <div><strong>Stripe ID:</strong> <code className="bg-gray-200 px-1 rounded text-xs">{subscription.stripeSubscriptionId.substring(0, 20)}...</code></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Subscription Result */}
      {subscriptionResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mt-4">
          <h3 className="text-xl font-semibold text-green-800 mb-2">Subscription Started Successfully!</h3>
          <div className="space-y-2 text-green-700">
            <p><strong>Status:</strong> {subscriptionResult.status}</p>
            {subscriptionResult.clientSecret && (
              <p><strong>Client Secret:</strong> <code className="bg-green-100 px-2 py-1 rounded text-sm break-all">{subscriptionResult.clientSecret}</code></p>
            )}
          </div>
          <button
            onClick={() => setSubscriptionResult(null)}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Clear Result
          </button>
        </div>
      )}

      {adminError && (
        <div className="bg-red-600 text-white rounded-xl p-6 mt-4">
          <h3 className="text-xl font-semibold mb-2">Admin Error</h3>
          <p className="text-base opacity-90">{adminError}</p>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionManagement;
